import React, { useRef, useEffect, useState, useCallback } from 'react';
// eslint-disable-next-line import/no-webpack-loader-syntax
import mainVertexShader from '!!raw-loader!./shaders/mainVertexShader.glsl';
// eslint-disable-next-line import/no-webpack-loader-syntax
import pillowFragmentShader from '!!raw-loader!./shaders/pillowFragmentShader.glsl';
// eslint-disable-next-line import/no-webpack-loader-syntax
import cubeFragmentShader from '!!raw-loader!./shaders/cubeFragmentShader.glsl';
// eslint-disable-next-line import/no-webpack-loader-syntax
import flowerFragmentShader from '!!raw-loader!./shaders/flowerFragmentShader.glsl';
// eslint-disable-next-line import/no-webpack-loader-syntax
import tennisFragmentShader from '!!raw-loader!./shaders/tennisFragmentShader.glsl';
// eslint-disable-next-line import/no-webpack-loader-syntax
import baseballFragmentShader from '!!raw-loader!./shaders/baseballFragmentShader.glsl';
// eslint-disable-next-line import/no-webpack-loader-syntax
import basketballFragmentShader from '!!raw-loader!./shaders/basketballFragmentShader.glsl';
// eslint-disable-next-line import/no-webpack-loader-syntax

interface ProjectionPreviewerProps {
  texture: string;
  offset: number;
  longitude: number;
  latitude: number;
  spin: number;
  projectionType: string;
  pinch: number;
  trim: number;
  petals: number;
  setPetals: React.Dispatch<React.SetStateAction<number>>;
  surplusY: number;
  setSurplusY: React.Dispatch<React.SetStateAction<number>>;
  surplusX: number;
  setSurplusX: React.Dispatch<React.SetStateAction<number>>;
  meridian: number;
  setMeridian: React.Dispatch<React.SetStateAction<number>>;
  beeLanding: number;
  setBeeLanding: React.Dispatch<React.SetStateAction<number>>;
  startSurplusX: number;
  endSurplusX: number;
  startSurplusY: number;
  endSurplusY: number;
}

function createShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error("Shader compilation failed:", type === gl.VERTEX_SHADER ? "VERTEX_SHADER" : "FRAGMENT_SHADER");
    console.error("Compilation error:", gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }
  
  return shader;
}

function createProgram(gl: WebGLRenderingContext, vertexShader: WebGLShader, fragmentShader: WebGLShader): WebGLProgram | null {
  const program = gl.createProgram();
  if (!program) return null;
  
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error("Program linking failed:", gl.getProgramInfoLog(program));
    gl.deleteProgram(program);
    return null;
  }
  
  return program;
}

function ProjectionPreviewer(props: ProjectionPreviewerProps) {
  const {
    texture,
    offset,
    longitude,
    latitude,
    spin,
    projectionType,
    pinch,
    trim,
    petals,
    surplusY,
    surplusX,
    beeLanding,
    meridian,
    startSurplusX,
    endSurplusX,
    startSurplusY,
    endSurplusY,
  } = props;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const glContextRef = useRef<WebGLRenderingContext | null>(null);
  const programRef = useRef<WebGLProgram | null>(null);
  const mainTextureRef = useRef<WebGLTexture | null>(null);
  const sdfTextureRef = useRef<WebGLTexture | null>(null);
  
  const [error, setError] = useState<string | null>(null);
  const [imageWidth, setImageWidth] = useState<number>(0);
  const [sdfLoaded, setSdfLoaded] = useState<boolean>(false);
  const isBaseballMode = projectionType.toLowerCase() === 'baseball';
  const isBasketballMode = projectionType.toLowerCase() === 'basketball';

  const cleanup = useCallback(() => {
    const gl = glContextRef.current;
    if (!gl) return;

    if (programRef.current) {
      gl.deleteProgram(programRef.current);
      programRef.current = null;
    }

    if (mainTextureRef.current) {
      gl.deleteTexture(mainTextureRef.current);
      mainTextureRef.current = null;
    }

    if (sdfTextureRef.current) {
      gl.deleteTexture(sdfTextureRef.current);
      sdfTextureRef.current = null;
    }
  }, []);

  const setupProgram = useCallback((gl: WebGLRenderingContext, vertexSource: string, fragmentSource: string) => {
    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexSource);
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
    
    if (!vertexShader || !fragmentShader) {
      setError('Failed to create shaders');
      return null;
    }
    
    const program = createProgram(gl, vertexShader, fragmentShader);
    if (!program) {
      setError('Failed to create program');
      return null;
    }

    return program;
  }, []);

  const setupGraphics = useCallback(async (gl: WebGLRenderingContext, program: WebGLProgram, image: HTMLImageElement) => {
    const canvas = canvasRef.current;
    if (!canvas) return false;

    // Different scaling logic for baseball and basketball vs other projections
if (isBaseballMode || isBasketballMode) {
  // For modes that need double height, consider maximum texture size and viewport constraints
  const MAX_TEXTURE_SIZE = gl.getParameter(gl.MAX_TEXTURE_SIZE);
  const MAX_VIEWPORT_SIZE = 4096;
  
  const targetHeight = image.height * 2;
  const scale = Math.min(
    1,
    MAX_TEXTURE_SIZE / image.width,
    MAX_TEXTURE_SIZE / targetHeight,
    MAX_VIEWPORT_SIZE / image.width,
    MAX_VIEWPORT_SIZE / targetHeight
  );

  const scaledWidth = Math.floor(image.width * scale);
  const baseHeight = Math.floor(image.height * scale);
  
  canvas.width = scaledWidth;
  canvas.height = baseHeight * 2;
  gl.viewport(0, 0, scaledWidth, baseHeight * 2);
} else {
  // For all other projections, maintain original behavior
  canvas.width = image.width;
  canvas.height = image.height;
  gl.viewport(0, 0, image.width, image.height);
}

    // Store dimensions for later use
    setImageWidth(image.width);

    // Create and set up the main texture
    const mainTexture = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, mainTexture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    mainTextureRef.current = mainTexture;

// Only set up SDF texture for baseball projection
if (isBaseballMode) {
  const sdfTexture = gl.createTexture();
  if (!sdfTexture) return false;
  
  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, sdfTexture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  
  const sdfImage = new Image();
  await new Promise<void>((resolve, reject) => {
    sdfImage.onload = () => {
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, sdfTexture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sdfImage);
      sdfTextureRef.current = sdfTexture;
      setSdfLoaded(true);
      resolve();
    };
    sdfImage.onerror = () => reject(new Error('Failed to load SDF texture'));
    sdfImage.src = '/textures/baseballmargin_sdf.png';
  });
}

// Set up SDF texture for basketball projection
if (isBasketballMode) {
  const sdfTexture = gl.createTexture();
  if (!sdfTexture) return false;
  
  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, sdfTexture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  
  const sdfImage = new Image();
  await new Promise<void>((resolve, reject) => {
    sdfImage.onload = () => {
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, sdfTexture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sdfImage);
      sdfTextureRef.current = sdfTexture;
      setSdfLoaded(true);
      resolve();
    };
    sdfImage.onerror = () => reject(new Error('Failed to load SDF texture'));
    sdfImage.src = '/textures/basketball_sdf4.png';
  });
}

return true;
}, [isBaseballMode, isBasketballMode]);

  const render = useCallback(() => {
    const gl = glContextRef.current;
    const program = programRef.current;
    if (!gl || !program) return;

    if ((isBaseballMode || isBasketballMode) && !sdfLoaded) {
      return;
    }

    gl.useProgram(program);

    const uniforms = {
      uImage: gl.getUniformLocation(program, "u_image"),
      uSdfTexture: gl.getUniformLocation(program, 'u_sdfTexture'),
      uOffset: gl.getUniformLocation(program, 'u_offset'),
      uLongitude: gl.getUniformLocation(program, 'u_longitude'),
      uLatitude: gl.getUniformLocation(program, 'u_latitude'),
      uSpin: gl.getUniformLocation(program, 'u_spin'),
      uPinch: gl.getUniformLocation(program, 'u_pinch'),
      uTrim: gl.getUniformLocation(program, 'u_trim'),
      uImageWidth: gl.getUniformLocation(program, 'u_imageWidth'),
      uSurplusY: gl.getUniformLocation(program, 'u_surplusY'),
      uSurplusX: gl.getUniformLocation(program, 'u_surplusX'),
      uBeeLanding: gl.getUniformLocation(program, 'u_beeLanding'),
      uPetals: gl.getUniformLocation(program, 'u_petals'),
      uMeridian: gl.getUniformLocation(program, 'u_meridian'),
      uStartSurplusX: gl.getUniformLocation(program, 'u_startSurplusX'),
      uEndSurplusX: gl.getUniformLocation(program, 'u_endSurplusX'),
      uStartSurplusY: gl.getUniformLocation(program, 'u_startSurplusY'),
      uEndSurplusY: gl.getUniformLocation(program, 'u_endSurplusY'),
      uSeamWidth: gl.getUniformLocation(program, 'u_seamWidth'),
    };

    gl.uniform1i(uniforms.uImage, 0);
    if (isBaseballMode || isBasketballMode) {
      gl.uniform1i(uniforms.uSdfTexture, 1);
    }

    gl.uniform1f(uniforms.uOffset, offset);
    gl.uniform1f(uniforms.uLongitude, longitude);
    gl.uniform1f(uniforms.uLatitude, latitude);
    gl.uniform1f(uniforms.uSpin, spin);
    gl.uniform1f(uniforms.uPinch, pinch);
    gl.uniform1f(uniforms.uTrim, trim);
    gl.uniform1f(uniforms.uImageWidth, imageWidth);
    gl.uniform1f(uniforms.uSurplusY, surplusY);
    gl.uniform1f(uniforms.uSurplusX, surplusX);
    gl.uniform1f(uniforms.uBeeLanding, beeLanding);
    gl.uniform1f(uniforms.uPetals, projectionType.toLowerCase() === 'basketball' ? 1 : petals);
    gl.uniform1f(uniforms.uMeridian, meridian);
    gl.uniform1f(uniforms.uStartSurplusX, startSurplusX);
    gl.uniform1f(uniforms.uEndSurplusX, endSurplusX);
    gl.uniform1f(uniforms.uStartSurplusY, startSurplusY);
    gl.uniform1f(uniforms.uEndSurplusY, endSurplusY);
    gl.uniform1f(uniforms.uSeamWidth, 0.02);

    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }, [
    isBaseballMode,
    sdfLoaded,
    offset,
    longitude,
    latitude,
    spin,
    pinch,
    trim,
    imageWidth,
    surplusY,
    surplusX,
    beeLanding,
    petals,
    meridian,
    startSurplusX,
    endSurplusX,
    startSurplusY,
    endSurplusY
  ]);

  const handleImageLoad = useCallback(async (image: HTMLImageElement) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.error('Canvas element not found');
      return;
    }

    cleanup();

    const gl = canvas.getContext('webgl', { preserveDrawingBuffer: true });
    if (!gl) {
      setError('WebGL not supported');
      return;
    }
    glContextRef.current = gl;

    // Select and set up shaders
    let vertexShaderSource = mainVertexShader;
    let fragmentShaderSource;

    const projectionLowerCase = projectionType.toLowerCase();
    if (projectionLowerCase === 'pillow') {
      fragmentShaderSource = pillowFragmentShader;
    } else if (projectionLowerCase === 'cube') {
      fragmentShaderSource = cubeFragmentShader;
    } else if (projectionLowerCase === 'flower') {
      fragmentShaderSource = flowerFragmentShader;
    } else if (projectionLowerCase === 'tennis') {
      fragmentShaderSource = tennisFragmentShader;
    } else if (projectionLowerCase === 'baseball') {
      fragmentShaderSource = baseballFragmentShader;
    } else if (projectionLowerCase === 'basketball') {
      fragmentShaderSource = basketballFragmentShader;
    } else {
      // Default equirectangular projection shader
      fragmentShaderSource = `
        precision mediump float;
        uniform sampler2D u_image;
        uniform float u_offset;
        uniform float u_longitude;
        uniform float u_latitude;
        uniform float u_spin;
        uniform float u_pinch;
        uniform float u_trim;
        uniform float u_imageWidth;
        uniform float u_meridian;
        varying vec2 v_texCoord;

        const float PI = 3.1415926535897932384626433832795;

        vec3 rotateX(vec3 p, float angle) {
          float s = sin(angle);
          float c = cos(angle);
          return vec3(p.x, c * p.y - s * p.z, s * p.y + c * p.z);
        }

        vec3 rotateY(vec3 p, float angle) {
          float s = sin(angle);
          float c = cos(angle);
          return vec3(c * p.x + s * p.z, p.y, -s * p.x + c * p.z);
        }

        vec3 rotateZ(vec3 p, float angle) {
          float s = sin(angle);
          float c = cos(angle);
          return vec3(c * p.x - s * p.y, s * p.x + c * p.y, p.z);
        }

        void main() {
          vec2 texCoord = v_texCoord;
          texCoord.y = 1.0 - texCoord.y;

          float offsetNormalized = u_offset / u_imageWidth;
          texCoord.x = mod(texCoord.x - offsetNormalized, 1.0);

          // Store original Y for trim check
          float originalY = texCoord.y;

          // Apply pinch transformation
          float pinchAmount = abs(u_pinch) / 100.0;
          if (pinchAmount > 0.0) {
            if (u_pinch < 0.0) {
              // Pinch from top
              if (texCoord.y > (1.0 - pinchAmount)) {
                gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
                return;
              }
              texCoord.y = texCoord.y / (1.0 - pinchAmount);
            } else {
              // Pinch from bottom
              if (texCoord.y < pinchAmount) {
                gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
                return;
              }
              texCoord.y = (texCoord.y - pinchAmount) / (1.0 - pinchAmount);
            }
          }

          // Apply trim transformation
          float trimAmount = abs(u_trim) / 100.0;
          if (trimAmount > 0.0) {
            if (u_trim < 0.0) {
              // Trim from top
              if (originalY > (1.0 - trimAmount)) {
                gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
                return;
              }
            } else {
              // Trim from bottom
              if (originalY < trimAmount) {
                gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
                return;
              }
            }
          }

          float lambda = (texCoord.x - 0.5) * 2.0 * PI;
          float phi = (texCoord.y - 0.5) * PI;

          lambda += u_meridian * PI / 180.0;
          lambda = mod(lambda + PI, 2.0 * PI) - PI;

          vec3 p = vec3(cos(phi) * cos(lambda), sin(phi), cos(phi) * sin(lambda));

          p = rotateY(p, u_longitude * PI / 180.0);
          p = rotateX(p, -u_latitude * PI / 180.0);
          p = rotateZ(p, u_spin * PI / 180.0);

          lambda = atan(p.z, p.x);
          phi = asin(p.y);

          // Convert back to texture coordinates
          texCoord.x = lambda / (2.0 * PI) + 0.5;
          texCoord.y = phi / PI + 0.5;

          gl_FragColor = texture2D(u_image, texCoord);
        }
      `;
    }

    // Create shader program
    const program = setupProgram(gl, vertexShaderSource, fragmentShaderSource);
    if (!program) return;
    programRef.current = program;

    // Set up graphics
    const positionBuffer = gl.createBuffer();
    const texCoordBuffer = gl.createBuffer();

    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    
    gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]), gl.STATIC_DRAW);

    // Set up attributes
    const positionAttributeLocation = gl.getAttribLocation(program, 'a_position');
    const texCoordAttributeLocation = gl.getAttribLocation(program, 'a_texCoord');

    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.enableVertexAttribArray(positionAttributeLocation);
    gl.vertexAttribPointer(positionAttributeLocation, 2, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
    gl.enableVertexAttribArray(texCoordAttributeLocation);
    gl.vertexAttribPointer(texCoordAttributeLocation, 2, gl.FLOAT, false, 0, 0);

    // Setup textures
    try {
      await setupGraphics(gl, program, image);
      render();
    } catch (err) {
      console.error('Failed to setup graphics:', err);
      setError('Failed to setup graphics');
    }
  }, [cleanup, setupProgram, setupGraphics, render, projectionType]);

  useEffect(() => {
    const image = new Image();
    image.src = texture;
    image.onload = () => handleImageLoad(image);
    image.onerror = (error) => {
      console.error('Failed to load image:', error);
      setError('Failed to load image');
    };

    return () => {
      cleanup();
    };
  }, [texture, handleImageLoad, cleanup]);

  useEffect(() => {
    render();
  }, [render]);

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div
      className="w-full h-full flex justify-center items-center overflow-hidden"
      onContextMenu={(e) => {
        e.preventDefault();
        const canvas = canvasRef.current;
        if (!canvas) {
          console.error('Canvas not found');
          return;
        }

        const gl = canvas.getContext('webgl', { preserveDrawingBuffer: true });
        if (!gl) {
          console.error('WebGL context not found');
          return;
        }

        // Read pixels from WebGL context with adjusted dimensions
        const width = canvas.width;
        const height = canvas.height;
        const pixels = new Uint8Array(width * height * 4);
        gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

        // Create a temporary canvas for the correct orientation
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = width;
        tempCanvas.height = height;
        const tempCtx = tempCanvas.getContext('2d');
        if (!tempCtx) {
          console.error('Failed to get 2D context');
          return;
        }

        // Put the pixels into ImageData and onto the canvas
        const imageData = new ImageData(new Uint8ClampedArray(pixels), width, height);
        tempCtx.putImageData(imageData, 0, 0);

        // Create another canvas to flip the image right side up
        const finalCanvas = document.createElement('canvas');
        finalCanvas.width = width;
        finalCanvas.height = height;
        const finalCtx = finalCanvas.getContext('2d');
        if (!finalCtx) {
          console.error('Failed to get final 2D context');
          return;
        }

        // Flip the image right side up
        finalCtx.translate(0, height);
        finalCtx.scale(1, -1);
        finalCtx.drawImage(tempCanvas, 0, 0);

        // Export the final canvas
        finalCanvas.toBlob((blob) => {
          if (!blob) {
            console.error('Failed to create blob');
            return;
          }

          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `projection_${projectionType.toLowerCase()}.png`;
          link.click();
          URL.revokeObjectURL(url);
        }, 'image/png');
      }}
    >
      <canvas
        ref={canvasRef}
        id="projectionCanvas"
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          width: 'auto',
          height: 'auto'
        }}
      />
    </div>
  );
}

export default ProjectionPreviewer;